import { Query } from '@varo/graphql-schema-types';
import notificationStatic from 'assets/icons/notification_static.png';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, StyleSheet, View } from 'react-native';
import GetUser from 'services/graphql/queries/User';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

/**
 * message = If no message is provided, the banner shows a greeting with the user's name or a default message.
 * If a message is provided, it displays that message.
 * description = If no description is provided, the banner shows a default message.
 * chatOutageMessage = Display chat outage message when chat is down
 */

interface ChatBannerProps {
  message?: string;
  description?: string;
  chatOutageMessage?: string;
}

const ChatBanner = ({
  message,
  chatOutageMessage,
  description,
}: ChatBannerProps) => {
  const { t: translatedText } = useTranslation('chatStrings');
  const [{ data: userData }] = useQuery<Query>({
    query: GetUser,
    requestPolicy: 'cache-only',
  });

  let headline = message;
  const bodyText = description?.length
    ? description
    : translatedText('banner.body');

  if (!message?.length) {
    const firstName = userData?.user?.customerInfo?.name?.first;

    headline = firstName
      ? `${translatedText('banner.bannerHi')} ${firstName}, ${translatedText('banner.title')}`
      : translatedText('banner.title');
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Image
          style={styles.image}
          source={notificationStatic as ImageSourcePropType}
          role="img"
        />
        <TitleMdText style={styles.title} numberOfLines={0}>
          {headline}
        </TitleMdText>
        <BodyBaseText style={styles.body} numberOfLines={0}>
          {bodyText}
        </BodyBaseText>
        {chatOutageMessage ? (
          <View style={styles.outageMessage}>
            <Icon
              iconKey="STATUS_WARNING"
              iconSize="MD"
              iconColor={theme.color.expressive.sunset}
            />
            <BodyBaseBoldText numberOfLines={0}>
              {chatOutageMessage}
            </BodyBaseBoldText>
          </View>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    textAlign: 'center',
  },

  container: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
    padding: theme.spacing.m,
  },

  content: {
    alignItems: 'center',
  },

  image: {
    height: 126,
    width: 126,
  },

  outageMessage: {
    display: 'flex',
    flexDirection: 'row',
    paddingTop: theme.spacing.m,
  },

  title: {
    marginBottom: theme.spacing.s,
    marginTop: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    textAlign: 'center',
  },
});

export default ChatBanner;
